/**
 * NutPop Animation - "Golden Treasure Eruption" (ENHANCED VERSION)
 * 
 * FRAMER MOTION VERSION: Ultimate treasure discovery experience!
 * - Peanut with worried face expression during pressure
 * - Caramel honey drip strings on explosion (tier 3+)
 * - Dollar sign coins with enhanced 3D spin (tier 4+)
 * - Sparkle trails following coins (tier 4+)
 * - Enhanced jackpot celebration with money emojis (tier 5+)
 * - Golden dust cloud on explosion
 * - Treasure chest lid popping (tier 5+)
 * - Screen gleam flash on reveal
 * 
 * Synchronized with sound effects.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useAnimationSound from '../../../hooks/useAnimationSound';
import { getPhasesForCandy } from './soundRegistry';

const NutPopAnimation = ({ intensity = 1, onComplete }) => {
    const [phase, setPhase] = useState('descending');

    // Sound integration
    const { playPhaseSound, preloadSounds } = useAnimationSound('NutPop', intensity, true);

    // Device-aware particle scaling for performance
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const particleMultiplier = isMobile ? 0.4 : 1;

    // Scaled effect counts with mobile optimization
    const crackLines = Math.floor((8 + intensity * 2) * particleMultiplier);
    const fragmentCount = Math.floor((8 + intensity * 3) * particleMultiplier);
    const coinCount = Math.floor((intensity * 5) * particleMultiplier);

    // Tier-based features
    const showPressure = intensity >= 1;
    const showTreasureCore = intensity >= 2;
    const showLightBeams = intensity >= 3;
    const showCoins = intensity >= 4;
    const showJackpot = intensity >= 5;
    const showScreenShake = intensity >= 4;
    const showCaramelStrings = intensity >= 3;
    const showSparkleTrails = intensity >= 4;
    const showWorriedFace = true;
    const showGoldenDust = intensity >= 3;
    const showMoneyEmojis = intensity >= 5;
    const showScreenGleam = intensity >= 4;

    // Animation timing
    const baseTiming = intensity >= 5 ? 1.15 : 1;

    // Colors
    const treasureColors = useMemo(() =>
        ['#FFD700', '#FFA500', '#FF8C00', '#FFB347', '#FFDF00', '#F0E68C'], []);

    // Stable ref for playPhaseSound to avoid re-triggering animation useEffect
    const playPhaseSoundRef = React.useRef(playPhaseSound);
    playPhaseSoundRef.current = playPhaseSound;

    // Stable ref for onComplete
    const onCompleteRef = React.useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Preload sounds (only once on mount)
    useEffect(() => {
        preloadSounds(getPhasesForCandy('NutPop'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Extended animation sequence
    useEffect(() => {
        const timers = [];

        playPhaseSoundRef.current('descending');

        timers.push(setTimeout(() => {
            setPhase('pressuring');
            playPhaseSoundRef.current('pressuring');
        }, 380 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('cracking');
            playPhaseSoundRef.current('cracking');
        }, 650 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('exploding');
            playPhaseSoundRef.current('exploding');
        }, 900 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('celebrating');
            playPhaseSoundRef.current('celebrating');
        }, 1300 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('complete');
            onCompleteRef.current?.();
        }, 2500 * baseTiming));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate particles
    const cracks = useMemo(() =>
        Array.from({ length: crackLines }, (_, i) => ({
            id: i,
            angle: (360 / crackLines) * i + Math.random() * 12,
            length: 35 + Math.random() * 40,
            delay: i * 0.02,
            thickness: 2 + Math.random() * 2,
        })), [crackLines]);

    const fragments = useMemo(() =>
        Array.from({ length: fragmentCount }, (_, i) => ({
            id: i,
            angle: Math.random() * 360,
            distance: 80 + Math.random() * 120,
            rotation: Math.random() * 1000 - 500,
            size: 14 + Math.random() * 20,
            delay: Math.random() * 0.12,
        })), [fragmentCount]);

    const coins = useMemo(() =>
        Array.from({ length: coinCount }, (_, i) => ({
            id: i,
            angle: Math.random() * 360,
            distance: 70 + Math.random() * 90,
            delay: Math.random() * 0.25,
            spin: 900 + Math.random() * 900,
            hasDollarSign: Math.random() > 0.3,
        })), [coinCount]);

    const lightBeams = useMemo(() =>
        Array.from({ length: 8 + intensity }, (_, i) => ({
            id: i,
            angle: (360 / (8 + intensity)) * i,
            delay: i * 0.04,
            width: 3 + Math.random() * 3,
        })), [intensity]);

    const caramelStrings = useMemo(() =>
        Array.from({ length: 5 }, (_, i) => ({
            id: i,
            startAngle: Math.random() * 360,
            length: 40 + Math.random() * 50,
            delay: i * 0.05,
        })), []);

    const goldenDust = useMemo(() =>
        Array.from({ length: 15 }, (_, i) => ({
            id: i,
            x: (Math.random() - 0.5) * 100,
            y: (Math.random() - 0.5) * 100,
            size: 2 + Math.random() * 4,
            delay: Math.random() * 0.3,
        })), []);

    const moneyEmojis = useMemo(() =>
        ['💰', '💵', '🤑', '💎', '✨'].map((emoji, i) => ({
            emoji,
            angle: (360 / 5) * i + Math.random() * 30,
            distance: 90 + Math.random() * 40,
            delay: 0.2 + i * 0.15,
        })), []);

    return (
        <div
            className={`relative flex items-center justify-center overflow-visible
                w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64
                ${showScreenShake && phase === 'exploding' ? 'animate-shake' : ''}
            `}
        >
            {/* === SCREEN GLEAM FLASH === */}
            <AnimatePresence>
                {showScreenGleam && phase === 'exploding' && (
                    <motion.div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: 'radial-gradient(circle at center, rgba(255,215,0,0.8) 0%, transparent 70%)',
                            zIndex: 100,
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{ duration: 0.3 }}
                    />
                )}
            </AnimatePresence>

            {/* === LIGHT BEAMS (enhanced) === */}
            <AnimatePresence>
                {showLightBeams && (phase === 'exploding' || phase === 'celebrating') && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        {lightBeams.map((beam) => (
                            <motion.div
                                key={beam.id}
                                className="absolute origin-bottom"
                                style={{
                                    width: beam.width,
                                    height: 160,
                                    background: 'linear-gradient(to top, rgba(255,215,0,0.9) 0%, rgba(255,215,0,0.3) 50%, transparent 100%)',
                                    transform: `rotate(${beam.angle}deg)`,
                                    filter: 'blur(1px)',
                                }}
                                initial={{ scaleY: 0, opacity: 0 }}
                                animate={{ scaleY: [0, 1, 0.9], opacity: [0, 0.9, 0.7] }}
                                transition={{ duration: 0.6, delay: beam.delay }}
                            />
                        ))}
                    </div>
                )}
            </AnimatePresence>

            {/* === GOLDEN PEANUT (enhanced with face) === */}
            <AnimatePresence>
                {phase !== 'exploding' && phase !== 'celebrating' && phase !== 'complete' && (
                    <motion.div
                        className="relative"
                        animate={
                            phase === 'descending' ? { y: [-60, 0], opacity: [0, 1], scale: [0.7, 1] } :
                                phase === 'pressuring' ? {
                                    scale: [1, 1.08, 0.96, 1.05, 0.98, 1.03, 1],
                                    transition: { repeat: Infinity, duration: 0.35 }
                                } :
                                    phase === 'cracking' ? { scale: 1.12 } : {}
                        }
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                    >
                        <motion.div
                            className="w-32 h-22 relative"
                            style={{
                                width: '8rem',
                                height: '5.5rem',
                                background: 'radial-gradient(ellipse at 30% 30%, #F5B041 0%, #E59400 40%, #B8860B 100%)',
                                borderRadius: '50%',
                                boxShadow: '0 10px 40px rgba(184, 134, 11, 0.6), inset 0 -5px 20px rgba(139, 90, 43, 0.5)',
                            }}
                        >
                            {/* Peanut texture lines */}
                            {[18, 38, 58, 78].map((top) => (
                                <div
                                    key={top}
                                    className="absolute w-3/4 h-[1px]"
                                    style={{
                                        background: 'rgba(139, 90, 43, 0.35)',
                                        top: `${top}%`,
                                        left: '12.5%',
                                    }}
                                />
                            ))}

                            {/* Shine */}
                            <div
                                className="absolute w-8 h-5 rounded-full"
                                style={{
                                    background: 'rgba(255,255,255,0.5)',
                                    top: '15%',
                                    left: '18%',
                                    filter: 'blur(3px)',
                                }}
                            />

                            {/* === WORRIED FACE === */}
                            {showWorriedFace && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    {/* Eyes */}
                                    <div className="flex gap-5 mb-1">
                                        {[0, 1].map((i) => (
                                            <div key={i} className="relative">
                                                {/* Worried eyebrows */}
                                                {(phase === 'pressuring' || phase === 'cracking') && (
                                                    <motion.div
                                                        className="absolute -top-2 w-5 h-1 bg-[#8B4513] rounded"
                                                        style={{
                                                            transformOrigin: i === 0 ? 'right' : 'left',
                                                        }}
                                                        animate={{
                                                            rotate: i === 0 ? [15, 25, 15] : [-15, -25, -15],
                                                        }}
                                                        transition={{ duration: 0.3, repeat: Infinity }}
                                                    />
                                                )}
                                                {/* Eye */}
                                                <motion.div
                                                    className="w-5 h-5 bg-white rounded-full flex items-center justify-center overflow-hidden"
                                                    style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.25)' }}
                                                    animate={phase === 'cracking' ? {
                                                        scaleY: [1, 0.6, 1],
                                                    } : {}}
                                                    transition={{ duration: 0.15, repeat: Infinity }}
                                                >
                                                    <motion.div
                                                        className="w-2.5 h-2.5 bg-[#4A3728] rounded-full"
                                                        animate={
                                                            phase === 'pressuring' ? { y: [-1, 1, -1] } :
                                                                phase === 'cracking' ? { scale: 0.7 } : {}
                                                        }
                                                        transition={{ duration: 0.2, repeat: Infinity }}
                                                    />
                                                </motion.div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Mouth */}
                                    <motion.div
                                        className="mt-1"
                                        animate={phase === 'cracking' ? { scale: 1.3 } : {}}
                                    >
                                        {phase === 'descending' ? (
                                            <div className="w-4 h-1 bg-[#8B4513] rounded" />
                                        ) : phase === 'pressuring' ? (
                                            <motion.div
                                                className="w-5 h-3 border-2 border-[#8B4513] rounded-b-full border-t-0"
                                                animate={{ scaleY: [1, 1.3, 1] }}
                                                transition={{ duration: 0.25, repeat: Infinity }}
                                            />
                                        ) : (
                                            <motion.div
                                                className="w-6 h-4 bg-[#8B4513] rounded-full"
                                                initial={{ scale: 0 }}
                                                animate={{ scale: [0, 1.2, 1] }}
                                                transition={{ duration: 0.1 }}
                                            />
                                        )}
                                    </motion.div>
                                </div>
                            )}

                            {/* Golden glow pulse */}
                            <motion.div
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background: 'radial-gradient(circle, rgba(255,215,0,0.4) 0%, transparent 70%)',
                                }}
                                animate={{ opacity: [0.3, 0.7, 0.3] }}
                                transition={{ duration: 0.8, repeat: Infinity }}
                            />

                            {/* Pressure pulse rings */}
                            {showPressure && phase === 'pressuring' && (
                                <>
                                    <motion.div
                                        className="absolute inset-0 rounded-full"
                                        style={{ border: '3px solid rgba(255,215,0,0.6)' }}
                                        animate={{ scale: [1, 1.3], opacity: [0.9, 0] }}
                                        transition={{ duration: 0.45, repeat: Infinity }}
                                    />
                                    <motion.div
                                        className="absolute inset-0 rounded-full"
                                        style={{ border: '2px solid rgba(255,215,0,0.4)' }}
                                        animate={{ scale: [1, 1.5], opacity: [0.7, 0] }}
                                        transition={{ duration: 0.45, delay: 0.15, repeat: Infinity }}
                                    />
                                </>
                            )}

                            {/* Cracks (enhanced) */}
                            {phase === 'cracking' && cracks.map((crack) => (
                                <motion.div
                                    key={crack.id}
                                    className="absolute left-1/2 top-1/2"
                                    style={{
                                        width: crack.thickness,
                                        height: crack.length,
                                        background: 'linear-gradient(to bottom, #FFFFFF, #FFD700)',
                                        transformOrigin: 'top center',
                                        rotate: crack.angle,
                                        boxShadow: '0 0 10px #FFD700, 0 0 20px rgba(255,215,0,0.5)',
                                    }}
                                    initial={{ scaleY: 0 }}
                                    animate={{ scaleY: [0, 1] }}
                                    transition={{ delay: crack.delay, duration: 0.08 }}
                                />
                            ))}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* === EXPLOSION EFFECTS === */}
            <AnimatePresence>
                {(phase === 'exploding' || phase === 'celebrating') && (
                    <>
                        {/* Blast flash */}
                        <motion.div
                            className="absolute w-44 h-44 rounded-full"
                            style={{ background: 'radial-gradient(circle, rgba(255,230,100,0.95) 0%, rgba(255,215,0,0.6) 40%, transparent 70%)' }}
                            initial={{ scale: 0 }}
                            animate={{ scale: [0, 2.8, 2.2], opacity: [1, 0.9, 0] }}
                            transition={{ duration: 0.45 }}
                        />

                        {/* Golden Dust Cloud */}
                        {showGoldenDust && goldenDust.map((dust) => (
                            <motion.div
                                key={`dust-${dust.id}`}
                                className="absolute rounded-full"
                                style={{
                                    width: dust.size,
                                    height: dust.size,
                                    background: '#FFD700',
                                    boxShadow: '0 0 4px #FFD700',
                                }}
                                initial={{ x: 0, y: 0, opacity: 0 }}
                                animate={{
                                    x: dust.x,
                                    y: dust.y,
                                    opacity: [0, 1, 0],
                                    scale: [0, 1.5, 0],
                                }}
                                transition={{ duration: 0.8, delay: dust.delay }}
                            />
                        ))}

                        {/* Treasure Core */}
                        {showTreasureCore && (
                            <motion.div
                                className="absolute w-18 h-18 rounded-full"
                                style={{
                                    width: '4.5rem',
                                    height: '4.5rem',
                                    background: 'radial-gradient(circle, #FFFFFF 0%, #FFD700 25%, #FFA500 100%)',
                                    boxShadow: '0 0 50px #FFD700, 0 0 100px rgba(255,165,0,0.8)',
                                }}
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.3, 1], opacity: [0, 1, 0.85] }}
                                transition={{ duration: 0.35 }}
                            />
                        )}

                        {/* Caramel/Honey Strings (tier 3+) */}
                        {showCaramelStrings && caramelStrings.map((string) => (
                            <motion.div
                                key={`caramel-${string.id}`}
                                className="absolute"
                                style={{
                                    width: 4,
                                    height: string.length,
                                    background: 'linear-gradient(to bottom, rgba(210,105,30,0.9), rgba(210,105,30,0.3))',
                                    borderRadius: '2px',
                                    transformOrigin: 'top center',
                                    transform: `rotate(${string.startAngle}deg)`,
                                }}
                                initial={{ scaleY: 0, opacity: 1 }}
                                animate={{
                                    scaleY: [0, 1.2, 1.5],
                                    y: [0, 20, 50],
                                    opacity: [1, 0.8, 0],
                                }}
                                transition={{ duration: 1, delay: string.delay, ease: 'easeOut' }}
                            />
                        ))}

                        {/* Shell Fragments (enhanced) */}
                        {fragments.map((f) => (
                            <motion.div
                                key={`frag-${f.id}`}
                                className="absolute rounded-sm"
                                style={{
                                    width: f.size,
                                    height: f.size * 0.55,
                                    background: 'linear-gradient(135deg, #F5B041 0%, #B8860B 100%)',
                                    boxShadow: '0 3px 10px rgba(184, 134, 11, 0.5)',
                                }}
                                initial={{ x: 0, y: 0, rotate: 0, scale: 0 }}
                                animate={{
                                    x: Math.cos((f.angle * Math.PI) / 180) * f.distance,
                                    y: Math.sin((f.angle * Math.PI) / 180) * f.distance + 50,
                                    rotate: f.rotation,
                                    scale: [0, 1.1, 0.7],
                                    opacity: [1, 1, 0],
                                }}
                                transition={{
                                    duration: 1.1,
                                    delay: f.delay,
                                    type: 'spring',
                                    stiffness: 70,
                                }}
                            />
                        ))}

                        {/* === COINS (enhanced with $ signs) === */}
                        {showCoins && coins.map((c) => (
                            <motion.div
                                key={`coin-${c.id}`}
                                className="absolute w-7 h-7 rounded-full flex items-center justify-center"
                                style={{
                                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)',
                                    border: '2px solid #DAA520',
                                    boxShadow: '0 3px 12px rgba(255, 215, 0, 0.6)',
                                }}
                                initial={{ x: 0, y: 0, rotateY: 0, scale: 0 }}
                                animate={{
                                    x: Math.cos((c.angle * Math.PI) / 180) * c.distance,
                                    y: Math.sin((c.angle * Math.PI) / 180) * c.distance + 35,
                                    rotateY: c.spin,
                                    scale: [0, 1.2, 0.9],
                                    opacity: [1, 1, 0],
                                }}
                                transition={{
                                    duration: 1.3,
                                    delay: c.delay,
                                }}
                            >
                                {/* Dollar sign */}
                                {c.hasDollarSign && (
                                    <span className="text-xs font-bold text-yellow-900/70">$</span>
                                )}
                                {/* Shine */}
                                <div className="absolute inset-1 rounded-full bg-yellow-200/40" />
                            </motion.div>
                        ))}

                        {/* Sparkle trails following coins (tier 4+) */}
                        {showSparkleTrails && coins.slice(0, 4).map((c, idx) => (
                            <motion.div
                                key={`sparkle-${idx}`}
                                className="absolute text-sm"
                                initial={{ x: 0, y: 0, opacity: 0 }}
                                animate={{
                                    x: Math.cos((c.angle * Math.PI) / 180) * (c.distance * 0.6),
                                    y: Math.sin((c.angle * Math.PI) / 180) * (c.distance * 0.6),
                                    opacity: [0, 1, 0],
                                    scale: [0.5, 1.2, 0],
                                }}
                                transition={{ duration: 0.6, delay: c.delay + 0.1 }}
                            >
                                ✨
                            </motion.div>
                        ))}

                        {/* === JACKPOT TEXT (enhanced) === */}
                        {showJackpot && phase === 'celebrating' && (
                            <motion.div
                                className="absolute text-4xl font-bold z-20"
                                style={{
                                    color: '#FFD700',
                                    textShadow: '3px 3px 0 #B8860B, -3px -3px 0 #B8860B, 3px -3px 0 #B8860B, -3px 3px 0 #B8860B, 0 0 30px #FFD700',
                                    fontFamily: 'Impact, sans-serif',
                                }}
                                initial={{ scale: 0, y: 30 }}
                                animate={{ scale: [0, 1.4, 1.1], y: [-25, -50] }}
                                transition={{ delay: 0.15, duration: 0.45 }}
                            >
                                JACKPOT!
                            </motion.div>
                        )}

                        {/* Money Emojis (tier 5+) */}
                        {showMoneyEmojis && phase === 'celebrating' && moneyEmojis.map((m, i) => (
                            <motion.div
                                key={`money-${i}`}
                                className="absolute text-2xl"
                                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                                animate={{
                                    x: Math.cos((m.angle * Math.PI) / 180) * m.distance,
                                    y: Math.sin((m.angle * Math.PI) / 180) * m.distance,
                                    opacity: [0, 1, 0],
                                    scale: [0, 1.3, 0.8],
                                    rotate: [0, 20, -10, 0],
                                }}
                                transition={{ delay: m.delay, duration: 0.8 }}
                            >
                                {m.emoji}
                            </motion.div>
                        ))}

                        {/* Sparkle ring (enhanced) */}
                        {showCoins && phase === 'celebrating' && (
                            <>
                                <motion.div
                                    className="absolute w-52 h-52 rounded-full"
                                    style={{ border: '4px solid #FFD700', opacity: 0.7 }}
                                    initial={{ scale: 0, opacity: 1 }}
                                    animate={{ scale: 2.2, opacity: 0 }}
                                    transition={{ duration: 0.9, repeat: 3 }}
                                />
                                <motion.div
                                    className="absolute w-40 h-40 rounded-full"
                                    style={{ border: '2px solid #FFA500' }}
                                    initial={{ scale: 0, opacity: 0.8 }}
                                    animate={{ scale: 1.8, opacity: 0 }}
                                    transition={{ duration: 0.9, delay: 0.2, repeat: 3 }}
                                />
                            </>
                        )}
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NutPopAnimation;
